import React, { useCallback } from "react";
import { Block, NativeESignatureBlockContent, SignatureRecord, SignatureRecordSignatureStatus, Signee } from "../types";
import { TooltipComponent } from "src/common/tooltip/component";
import { ControlPanelSwitchButton } from "../components/control-panel/switch";
import { PaintbrushLightIcon, PendingSignatureDocumentLightNeueIcon, PoweredByDropboxSignLightNeueIcon, RefreshLightIcon, SignedDocumentLightNeueIcon, SquiggleStrongIcon, UsersLightIcon, UsersStrongIcon } from "src/monet/icons";
import { formatUtcDateTimeToLocal } from "src/utils/date";
import { NeueCanvasButton } from "../components/neue-button/canvas";
import { useNativeSellerESignature } from "./use-native-e-signature.hook";
import classNames from "classnames";
import produce from "immer";
import { ToggleButton } from "../components/dashboard/create-journey-modal";
import { ControlPanelActionButton } from "../components/control-panel/action-button";
import { NeueButtonBase } from "../components/neue-button/base";
import { ControlPanelShell } from "../components/control-panel/shell";
import { getBlockKey } from "../block-control-panel";
import { NeueTooltip } from "../components/tooltip";
import { Nullable } from "src/types/nullable.type";
import { NeueJourneyLoadingSpinner } from "../components/loading/journey-spinner";
import { RecipientForm } from "./recipient-form";
import { NeueJourneyButton } from "../components/neue-button/journey";
import { OPTIMISTIC_SIGNED_SIGNATURE_STATES, isOptimisticallySignedBySigner } from "./utils";
import { useEditorStore } from "../editor-store";


const ControlPanelDivider = () => {
  return <div className='w-full h-[1px] bg-neue-journey-fg-10'></div>
}

type SignerProps = {
  pos: number;
  status: 'ready_to_sign' | SignatureRecordSignatureStatus;
  onSignClick?: () => void;
  signerEmail?: string;
  signedAt?: Nullable<Date>;
}
const SignerRow = ({ pos, status, signerEmail, signedAt, onSignClick }: SignerProps) => {
  let tooltip;
  if (status === 'signed') {
    tooltip = `Signed at ${formatUtcDateTimeToLocal(signedAt)}`;
  } else if (status === 'pending_signed') {
    tooltip = 'Signed';
  } else {
    tooltip = 'Awaiting signature';
  }

  let content;
  if (pos === 0 && status === 'ready_to_sign') {
    content = <a onClick={onSignClick} href='javascript:void(0)' className='underline'>You need to sign</a>
  } else {
    content = signerEmail;
  }

  return (
    <NeueTooltip
      offset={20}
      placement='left'
      tooltipContent={tooltip}
    >
      <div className='w-full h-10 flex flex-row gap-2 py-1 px-2 items-center text-neue-journey-medium'>
        <div>
          {
            OPTIMISTIC_SIGNED_SIGNATURE_STATES.has(status)
              ? <SignedDocumentLightNeueIcon className="w-4 h-4" />
              : <SquiggleStrongIcon className='w-4 h-4' />
          }
        </div>
        <div>
          {content}
        </div>
      </div>
    </NeueTooltip>
  )
}
interface Props {
  block: Block;
  journey: any;
  onHighlightedChange: (highlighted: boolean) => void;
  onPinnedChange: (pinned: boolean) => void;
  onSignatureRecordChange: (signatureRecord: SignatureRecord) => void;
  onSigneesChange: (signers: Signee[]) => void;
}

type SignatureBlockStage = 'initial' | 'pending_signatures' | 'confirmation' | 'signed';

export const EditNativeESignatureControlPanel = ({ block, onHighlightedChange, onPinnedChange, journey, onSignatureRecordChange, onSigneesChange }: Props) => {
  const content = block.content as NativeESignatureBlockContent;

  const selectedBlockId = useEditorStore((state) => state.selectedBlockId);

  const [downloading, setDownloading] = React.useState(false);
  const [signing, setSigning] = React.useState(false);

  const signatureRecord = content.signature_record;
  const signedBySomeone = signatureRecord && (signatureRecord.status === 'signed' || signatureRecord.status === 'presigned_by_seller');
  const signedByAll = signatureRecord && signatureRecord.status === 'signed';

  let initalStage: SignatureBlockStage;
  if (content.signees && content.signees.length > 0) {
    initalStage = 'pending_signatures';
  } else {
    initalStage = 'initial';
  }

  const [stage, setStage] = React.useState<SignatureBlockStage>(initalStage);

  const { onClick: onSignClick, modal } = useNativeSellerESignature(content.signature_record, 'web', journey.uuid, onSignatureRecordChange);

  const pdfUrl = signatureRecord?.signed_journey_pdf_url;

  let panelContent = null;

  const saveRecipient = useCallback((recipient: { name: string, email: string }) => {
    onSigneesChange(produce(content.signees, signees => {
      if (signees.length === 1) {
        signees[0] = recipient;
      } else {
        signees.push(recipient);
      }
    }));
    setStage('pending_signatures');
  }, [onSigneesChange, content.signees]);

  const openSignModal = useCallback(async () => {
    try {
      setSigning(true);
      await onSignClick(
        content.signees[0],
        () => {
          setStage('pending_signatures');
          setSigning(false);
        },
      )
    } catch (e) {
      console.error(e);
      setSigning(false)
    }
  }, [onSignClick, content.signees]);

  const downloadSignedPdf = useCallback(async () => {
    if (signatureRecord === undefined) return;
    if (!pdfUrl) return;

    setDownloading(true);
    const response = await fetch(pdfUrl);
    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);
    let alink = document.createElement('a');
    alink.href = fileUrl;
    alink.download = (journey.name || signatureRecord.uuid) + ".pdf";
    alink.click();

    setDownloading(false);
  }, [pdfUrl])

  const [activeTab, setActiveTab] = React.useState<'signees' | 'design'>('signees');

  let subtitle;
  switch (stage) {
    case 'initial': {
      subtitle = 'Who are you sending this Journey to? We use these details to create the block and no emails are sent.';
      panelContent = <RecipientForm onRecipientAdded={saveRecipient} recipientName={content.signees[0].name} recipientEmail={content.signees[0].email} />
      break;
    };
    case 'pending_signatures': {
      subtitle = undefined;
      let tabContent = null;
      if (activeTab === 'signees') {
        tabContent = <div className='flex flex-col gap-4'>
          <div className='text-neue-journey-small'>
            {
              !content.signature_record ? (
                'When a Journey is signed by either party it is immediately locked to changes'
              ) : content.signature_record.signatures.every(s => OPTIMISTIC_SIGNED_SIGNATURE_STATES.has(s.status_code)) ? (
                'Both parties have signed this Journey and it is now locked'
              ) : content.signature_record.signatures[0].status_code === 'awaiting_signature' ? (
                'This Journey is now locked, finish signing'
              ) : isOptimisticallySignedBySigner(content.signature_record, 0) ? (
                'A party has signed this Journey and it is now locked to further changes'
              ) : (
                'When a Journey is signed by either party it is immediately locked to changes'
              )
            }
          </div>
          <div className="flex flex-col gap-1">
            {
              (!content.signature_record || content.signature_record.status === 'pending') ? (
                <>
                  <SignerRow pos={0} status='ready_to_sign' onSignClick={() => content.signature_record ? openSignModal() : setStage('confirmation')} />
                  <SignerRow pos={1} status='awaiting_signature' signerEmail={content.signees[0].email} />
                </>
              ) : (
                content.signature_record?.signatures.map((signature, pos) => {
                  const signedAt = signature.signed_at ? new Date(signature.signed_at * 1000) : null;
                  return <SignerRow pos={pos} status={signature.status_code} signerEmail={signature.signer_email_address} signedAt={signedAt} />
                })
              )
            }
          </div>
          <ControlPanelDivider />
          {
            signedByAll
              ? (
                <NeueJourneyButton 
                  onClick={downloadSignedPdf}
                  disabled={downloading}
                  type='button' 
                  className='h-10 w-full'>

                  {
                    downloading
                      ? <NeueJourneyLoadingSpinner size='16' />
                      : 'Download signed PDF'
                  }
                </NeueJourneyButton>
              )
              : (
                <ControlPanelActionButton
                  disabled={signedBySomeone}
                  label='Change recipient'
                  iconComponent={RefreshLightIcon}
                  onClick={() => setStage('initial')}
                  tooltip={signedBySomeone ? 'Cannot change as someone has signed already' : undefined}
                />
              )
          }
        </div>
      } else if (activeTab === 'design') {
        tabContent = <div>
          <TooltipComponent
            tooltipContent='Go old school and attach yellow tape'
            placement='left'
            styling='tooltip'
            theme='journey'
            offset={24}
            anchorClassName='flex-1'
            containerClassName='flex-1'
          >
            <ControlPanelSwitchButton
              checked={content.highlighted}
              onChange={(checked: boolean) => onHighlightedChange(checked)}
              label='Highlight block'
            />
          </TooltipComponent>
          {/* <TooltipComponent
            tooltipContent='Pin the click to sign button Journey to the page'
            placement='left'
            styling='tooltip'
            theme='journey'
            offset={24}
            anchorClassName='flex-1'
            containerClassName='flex-1'
          >
            <ControlPanelSwitchButton
              checked={content.pinned}
              onChange={(checked: boolean) => onPinnedChange(checked)}
              label='Pin button'
            />
          </TooltipComponent> */}
        </div>
      }
      panelContent = <div className='flex flex-col gap-4'>
        <div className='flex flex-row gap-2'>
          <ToggleButton disabled={false} selected={activeTab === 'signees'} label={(
            <div className="flex items-center">
              <div className='w-[60px]'>Signees</div>
              <UsersLightIcon className='w-5 h-5' />
            </div>
          )} onClick={() => setActiveTab('signees')} />
          <ToggleButton disabled={false} selected={activeTab === 'design'} label={(
            <div className="flex items-center">
              <div className='w-[60px]'>Design</div>
              <PaintbrushLightIcon className='w-5 h-5' />
            </div>
          )} onClick={() => setActiveTab('design')} />
        </div>
        {tabContent}
      </div>
      break;
    }
    case 'confirmation': {
      subtitle = 'A Journey acts as a contract - by signing this Journey we will lock the Journey to further changes';
      panelContent = <div className='w-full h-10 flex flex-row gap-2'>
        <NeueJourneyButton
          onClick={() => setStage('pending_signatures')} 
          className='h-10 w-auto' 
          disabled={signing}
          variant="secondary"
        >
          <span className='text-neue-journey-fg'>Cancel</span>
        </NeueJourneyButton>

        <NeueJourneyButton 
          onClick={openSignModal} 
          disabled={signing}
          type='button' 
          className='h-10 w-[130px]'>
          {
            signing ? <NeueJourneyLoadingSpinner size='16' /> : 'Sign and lock'
          }
        </NeueJourneyButton>
      </div>
      break;
    }
  }

  return (
    <ControlPanelShell
      title='e-Signature'
      subtitle={subtitle}
      key={getBlockKey(block)}
      size='regular'
      active={block.id === selectedBlockId}
    >
      <div className='flex flex-col gap-4 items-center w-full'>
        {panelContent}
        {modal}
      </div>
    </ControlPanelShell>
  );
}